import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { CommonFormElementSize, CommonSize } from '@/common/types'
import { LabeledChoiceElement } from '@/internal/mixin/labeledChoiceElement'

import styles from './_styles/radio-button.scss?inline'

/**
 * `dap-ds-radio-button`
 * @summary A radio button is a graphical control element that allows the user to choose only one of a predefined set of mutually exclusive options.
 * @element dap-ds-radio-button
 * @title - Radio button
 *
 * @event {{ checked: boolean, value: string, disabled: boolean, type: 'radio' }} dds-change - Fired when the radio button is checked.
 * @event {{ void }} dds-blur - Emitted when the radio button loses focus.
 * @event {{ void }} dds-focus - Emitted when the radio button gains focus.
 * @event {{ checked: boolean, value: string, disabled: boolean }} dds-input - Emitted when the radio button receives input.
 *
 * @property {string} name - The name of the radio button.
 * @property {string} value - The value of the radio button.
 * @property {boolean} checked - Whether the radio button is checked.
 * @property {string} label - The label of the radio button.
 * @property {string} description - The description of the radio button.
 * @property {boolean} disabled - Whether the radio button is disabled.
 * @property {boolean} required - Whether the radio button is required.
 * @property {'xs' | 'sm' | 'md' | 'lg'} size - The size of the radio button.
 * @property {'left' | 'right'} labelPlacement - The placement of the label.
 * @property {'top' | 'bottom'} descriptionPlacement - The placement of the description.
 * @property {boolean} subtle - The weight of the label.
 * @property {string} feedback - The feedback of the radio button.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the radio button.
 * @property {boolean} invalid - The invalid state of the radio button.
 * @property {boolean} optional - The optional state of the radio button.
 * @property {string} optionalLabel - The optional label of the radio button.
 * @property {boolean} preventDefault - Whether the radio button should prevent the default action.
 * @property {boolean} focusable - Whether the radio button is focusable.
 * @property {'normal' | 'background'} type - The type of the radio button.
 * @property {boolean} border - This sets up a border around the radio button, when true.
 *
 * @csspart base - The main radio button container.
 * @csspart wrapper - The wrapper of the radio button.
 * @csspart base-label - The main label container.
 * @csspart label - The label of the radio button.
 * @csspart input - The native input of the radio button.
 * @csspart control - The control of the radio button.
 * @csspart label-container - The label container of the radio button.
 * @csspart description - The description of the radio button.
 *
 * @cssproperty --dds-radio-size - Sets the size of the radio button. Default: var(--dds-spacing-600)
 * @cssproperty --dds-radio-border-width - Sets the border width of the radio button. Default: var(--dds-border-width-large)
 * @cssproperty --dds-radio-border-radius - Sets the border radius of the radio button. Default: var(--dds-radius-rounded)
 * @cssproperty --dds-radio-border-color - Sets the border color of the radio button. Default: var(--dds-border-neutral-base)
 * @cssproperty --dds-radio-background-color - Sets the background color of the radio button. Default: var(--dds-background-neutral-base)
 * @cssproperty --dds-radio-hover-border-color - Sets the border color of the radio button on hover. Default: var(--dds-border-neutral-medium)
 * @cssproperty --dds-radio-hover-background-color - Sets the background color of the radio button on hover. Default: var(--dds-background-neutral-medium)
 * @cssproperty --dds-radio-active-border-color - Sets the border color of the radio button when active. Default: var(--dds-border-neutral-strong)
 * @cssproperty --dds-radio-active-background-color - Sets the background color of the radio button when active. Default: var(--dds-background-neutral-strong)
 * @cssproperty --dds-radio-checked-background-color - Sets the background color of the checked radio button. Default: var(--dds-background-brand-base-inverted)
 * @cssproperty --dds-radio-checked-hover-background-color - Sets the background color of the checked radio button on hover. Default: var(--dds-background-brand-medium-inverted)
 * @cssproperty --dds-radio-checked-active-background-color - Sets the background color of the checked radio button when active. Default: var(--dds-background-brand-strong-inverted)
 * @cssproperty --dds-radio-disabled-background-color - Sets the background color of the disabled radio button. Default: var(--dds-background-neutral-disabled)
 * @cssproperty --dds-radio-icon-background-color - Sets the background color of the radio button icon. Default: var(--dds-transparent-white-strong-static)
 * @cssproperty --dds-radio-disabled-icon-background-color - Sets the background color of the disabled radio button icon. Default: var(--dds-background-neutral-stronger)
 * @cssproperty --dds-radio-invalid-border-color - Sets the border color of the invalid radio button. Default: var(--dds-border-negative-base)
 * @cssproperty --dds-radio-invalid-background-color - Sets the background color of the invalid radio button. Default: var(--dds-background-negative-base)
 * @cssproperty --dds-radio-invalid-hover-border-color - Sets the border color of the invalid radio button on hover. Default: var(--dds-border-negative-medium)
 * @cssproperty --dds-radio-invalid-hover-background-color - Sets the background color of the invalid radio button on hover. Default: var(--dds-background-negative-medium)
 * @cssproperty --dds-radio-invalid-active-border-color - Sets the border color of the invalid radio button when active. Default: var(--dds-border-negative-strong)
 * @cssproperty --dds-radio-invalid-active-background-color - Sets the background color of the invalid radio button when active. Default: var(--dds-background-negative-strong)
 * @cssproperty --dds-radio-invalid-checked-background-color - Sets the background color of the invalid checked radio button. Default: var(--dds-background-negative-base-inverted)
 * @cssproperty --dds-radio-invalid-checked-hover-background-color - Sets the background color of the invalid checked radio button on hover. Default: var(--dds-background-negative-medium-inverted)
 * @cssproperty --dds-radio-invalid-checked-active-background-color - Sets the background color of the invalid checked radio button when active. Default: var(--dds-background-negative-strong-inverted)
 * @cssproperty --dds-radio-icon-size - Sets the size of the radio button icon. Default: var(--dds-spacing-300)
 */
@customElement('dap-ds-radio-button')
export default class DapDSRadioButton extends LabeledChoiceElement {
  @query('#input') private readonly input!: HTMLInputElement

  /** Whether the radio button is focusable */
  @property({ type: Boolean }) focusable = false

  /** Whether the radio button should prevent the default action */
  @property({ type: Boolean, reflect: true }) preventDefault: boolean = false

  /** This sets up a border around the radio button, when true. */
  @property({ type: Boolean }) border: boolean = false

  /** The type of the radio button
   * @type {'normal' | 'background'}
   */
  @property({ type: String }) type: 'normal' | 'background' = 'normal'

  static readonly styles = unsafeCSS(styles)

  /**
   * Gets the aria-describedby attribute value by combining description and feedback IDs.
   * @returns {string | undefined} Space-separated IDs or undefined if none exist
   * @private
   */
  private getAriaDescribedBy(): string | undefined {
    const ids: string[] = []

    if (this.description) {
      ids.push('description')
    }

    if (this.feedback) {
      ids.push('feedback')
    }

    return ids.length > 0 ? ids.join(' ') : undefined
  }

  /**
   * Handles click events on the radio button.
   * Manages the checked state and emits change events.
   * @private
   */
  private handleClick() {
    // Early return if click should be prevented or component is disabled
    if (this.preventDefault || this.disabled) {
      return
    }

    this.checked = true

    const { checked, value, disabled } = this

    // Emit change event with null safety
    try {
      this.emit('dds-change', {
        checked,
        value: value || '',
        disabled,
        type: 'radio',
      })
    } catch (error) {
      console.error('Error emitting radio button change event:', error)
    }

    // Update validity state with error handling
    try {
      this.setValidity()
    } catch (error) {
      console.error('Error setting radio button validity:', error)
    }
  }

  formResetCallback() {
    this.internals.setValidity({})
    this.internals.setFormValue(this.defaultValue)
    this.value = this.defaultValue
    this.checked = this.defaultValue === this.value
  }

  /**
   * Handles input events on the radio button.
   * Emits the dds-input event with current radio button state.
   * @private
   */
  private handleInput() {
    const { checked, value, disabled } = this

    const event = new CustomEvent('dds-input', {
      bubbles: true,
      cancelable: false,
      composed: true,
      detail: { checked, value, disabled },
    })

    this.dispatchEvent(event)
  }

  /**
   * Handles keyboard events on the radio button.
   * Provides enhanced keyboard navigation and accessibility.
   * @param {KeyboardEvent} event - The keyboard event
   * @private
   */
  private handleKeyDown(event: KeyboardEvent): void {
    // Handle Space key to select radio button (in addition to default behavior)
    if (event.code === 'Space') {
      // Allow default Space behavior for radio input
      // This ensures proper accessibility and form behavior
      return
    }

    // Handle Enter key to select radio button (custom behavior)
    if (event.code === 'Enter') {
      event.preventDefault()
      this.handleClick()
    }
  }

  public get focusElement(): HTMLInputElement {
    if (!this.input) {
      throw new Error(
        'Radio button input element not found. Component may not be properly initialized.',
      )
    }
    return this.input
  }

  /**
   * Programmatically focuses the radio button input element.
   * @public
   */
  public focus(): void {
    if (this.input && !this.disabled) {
      this.input.focus()
    }
  }

  /**
   * Programmatically blurs the radio button input element.
   * @public
   */
  public blur(): void {
    if (this.input) {
      this.input.blur()
    }
  }

  /**
   * Handles invalid events on the radio button input.
   * Prevents the default browser validation UI from showing and manages invalid state.
   * @param {Event} event - The invalid event
   * @private
   */
  handleInvalid(event: Event) {
    event.preventDefault()
    // Set invalid state if not already set
    if (!this.invalid) {
      this.invalid = true
    }
  }

  render() {
    return html`
      <div part="base">
        <label
          part="base-label"
          id="label"
          class=${clsx('radio choice', `radio--${this.size}`, {
            'radio--checked': this.checked,
            'radio--disabled': this.disabled,
            'radio--required': this.required,
            'choice__label--right': this.labelPlacement === 'right',
            'choice__label--left': this.labelPlacement === 'left',
          })}>
          <div
            part="wrapper"
            class=${clsx('input__wrapper', {
              'input__wrapper--background': this.type === 'background',
              'input__wrapper--border': this.border,
            })}>
            <input
              part="input"
              type="radio"
              class=${clsx('radio__input', {
                'radio__input--background': this.type === 'background',
              })}
              name=${ifDefined(this.name ? this.name : undefined)}
              id="input"
              value=${ifDefined(this.value)}
              .checked=${this.checked}
              .disabled=${this.disabled}
              .required=${this.required}
              tabindex=${this.checked || this.focusable ? '0' : '-1'}
              aria-label=${ifDefined(this.ariaLabel) || ''}
              aria-describedby=${ifDefined(this.getAriaDescribedBy())}
              aria-checked=${this.checked ? 'true' : 'false'}
              .aria-invalid=${this.invalid ? 'true' : null}
              @invalid=${this.handleInvalid}
              @click=${this.handleClick}
              @input=${this.handleInput}
              @keydown=${this.handleKeyDown}
              @blur=${super.onBlur}
              @focus=${super.onFocus} />
            <span
              part="control"
              class=${clsx('input__control', {
                'input__control--disabled': this.disabled,
                'input__control--invalid': this.invalid,
                'input__control--checked': this.checked,
              })}>
              <slot name="iconcontent">
                <span class="radio__icon"></span>
              </slot>
              <span
                part="high-contrast"
                class=${clsx(
                  'input__control-high-contrast',
                  `input__control-high-contrast--${this.size}`,
                  {
                    'input__control-high-contrast--disabled': this.disabled,
                  },
                )}></span>
            </span>

            <div
              part="label-container"
              class=${clsx('choice__label-container', {
                'choice__label-container--disabled': this.disabled,
                'choice__label--bottom': this.descriptionPlacement === 'top',
                'choice__label--top': this.descriptionPlacement === 'bottom',
              })}>
              ${this.label &&
              html`<dap-ds-form-label
                id="label"
                class="label"
                part="label"
                .size=${this.size}
                .required=${this.required}
                .optional=${this.optional}
                .optionalLabel=${this.optionalLabel}
                .disabled=${this.disabled}
                label=${ifDefined(this.label)}
                subtle
                renderAs="span">
              </dap-ds-form-label>`}
              ${this.description &&
              html`
                <dap-ds-typography
                  class="description"
                  variant="description"
                  part="description"
                  size=${ifDefined(this.size as CommonFormElementSize)}
                  id="description">
                  ${this.description}
                </dap-ds-typography>
              `}
            </div>
          </div>
          ${this.feedback &&
          html`
            <dap-ds-feedback
              part="feedback"
              feedbackType=${ifDefined(this.feedbackType)}
              feedbackSize=${ifDefined(this.size) as CommonSize}
              id="feedback"
              .subtle=${this.subtle}>
              ${this.feedback}
            </dap-ds-feedback>
          `}
        </label>
      </div>
    `
  }
}
